import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { CheckCircle } from "@phosphor-icons/react";

const AGREEMENT_SECTIONS = [
    {
        title: "Independent Contractor Status",
        text: "You're engaged as an independent contractor (1099) — not an employee. You're responsible for your own taxes, licenses, and insurance.",
    },
    {
        title: "Scope — Premium Residential Cleaning Only",
        text: "You agree to offer only white-glove residential cleaning services through The Butler Room — including one-time, weekly, bi-weekly, and monthly cleans, move-in/move-out deep cleans, garage cleaning, and add-ons (exterior windows, oven interior, refrigerator interior, laundry & folding). Handyman, plumbing, electrical, landscaping, HVAC, roofing, and other non-cleaning services are not offered through this platform.",
    },
    {
        title: "Compensation — 70 / 30 Split, $0 Membership",
        text: "There is NO monthly membership fee and NO per-lead fee. For each completed cleaning job, you receive 70% of the client-paid price; The Butler Room retains 30% as its platform fee. Client tips are passed through to you at 100%.",
    },
    {
        title: "How Payment Works — Weekly Payouts",
        text: "Clients pay The Butler Room directly through Stripe. We pay you out every Friday for cleaning jobs that were completed and accepted by the client during the preceding Monday–Sunday window. Acceptance is established when the client gives a 👍 rating or when 7 days pass with no dispute, whichever comes first.",
    },
    {
        title: "White-Glove Quality Standards",
        text: "Arrive on time in clean attire, follow the platform checklist for the booked service tier, treat client property with care, and secure the home before leaving. Repeated complaints, no-shows, or substandard work are grounds for immediate termination.",
    },
    {
        title: "Insurance & Background Check",
        text: "Maintain at least $300k general liability insurance. You consent to a background check and identity verification at onboarding and from time to time during the term.",
    },
    {
        title: "Tax Reporting (W-9)",
        text: "You'll provide a completed IRS Form W-9 before your first payout. We'll issue a 1099-NEC by Jan 31 of the following year if reporting thresholds are met.",
    },
    {
        title: "Non-Circumvention",
        text: "For 12 months after the last job referred through the platform, you agree not to directly solicit off-platform cleaning work from any client introduced by The Butler Room (clients are free to engage you independently without solicitation).",
    },
    {
        title: "Termination",
        text: "Either party may end this agreement at any time by written notice (email is sufficient). No outstanding membership or lead fees apply.",
    },
    {
        title: "Governing Law",
        text: "Florida law applies. Disputes go to mediation first in Orange County, FL, then to Florida state or federal courts. Prevailing party recovers reasonable attorney's fees.",
    },
];

export default function PartnerApplyPage() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        contractor_name: "", business_name: "", email: "", phone: "",
        address: "", ein_last4: "", services: "", service_area: "",
    });
    const [signature, setSignature] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (!agreed) { setError("You must agree to the terms to submit."); return; }
        if (!signature) { setError("Please sign in the box."); return; }
        setSubmitting(true);
        try {
            await http.post("/partners/apply", { ...form, signature_data_url: signature, agreed: true });
            setSuccess(true);
        } catch (err) { setError(formatApiError(err)); }
        finally { setSubmitting(false); }
    }

    if (success) {
        return (
            <div data-testid="partner-success" className="max-w-2xl mx-auto px-6 py-24 text-center">
                <CheckCircle size={72} weight="duotone" className="text-gold mx-auto" />
                <h1 className="font-serif text-5xl mt-6">Application received.</h1>
                <p className="text-slate-400 mt-4">A signed copy of the agreement has been emailed to <span className="text-gold">{form.email}</span>. We&apos;ll review and reach out within 2 business days.</p>
                <Link to="/" className="btn-gold inline-block mt-10">Back to home</Link>
            </div>
        );
    }

    return (
        <div data-testid="partner-apply-page" className="max-w-4xl mx-auto px-6 sm:px-10 py-12">
            <div className="label-eyebrow mb-3">/ Apply to the network</div>
            <h1 className="font-serif text-4xl sm:text-5xl mb-10">Partner <span className="italic gold-text">application</span></h1>

            <form onSubmit={submit} className="space-y-10">
                {/* Section 1: About you */}
                <section className="bg-slate-900/40 border border-white/5 p-6 sm:p-8 rounded-sm">
                    <h2 className="font-serif text-2xl mb-6">About you</h2>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Your full name *" testid="partner-name" value={form.contractor_name} onChange={(v) => set("contractor_name", v)} />
                        <Field label="Business / LLC name" testid="partner-business" value={form.business_name} onChange={(v) => set("business_name", v)} />
                        <Field label="Email *" type="email" testid="partner-email" value={form.email} onChange={(v) => set("email", v)} />
                        <Field label="Phone *" type="tel" testid="partner-phone" value={form.phone} onChange={(v) => set("phone", v)} />
                        <Field label="Business address *" testid="partner-address" value={form.address} onChange={(v) => set("address", v)} className="sm:col-span-2" />
                        <Field label="EIN or last 4 of SSN *" testid="partner-ein" value={form.ein_last4} onChange={(v) => set("ein_last4", v)} maxLength={9} />
                        <Field label="Service area (city / zip codes) *" testid="partner-area" value={form.service_area} onChange={(v) => set("service_area", v)} />
                        <Field label="Cleaning services you offer *" testid="partner-services" value={form.services} onChange={(v) => set("services", v)} className="sm:col-span-2" placeholder="e.g. weekly cleaning, move-in/move-out deep clean, exterior windows, oven interior, laundry & folding" />
                    </div>
                </section>

                {/* Section 2: Review the agreement */}
                <section className="bg-slate-900/40 border border-white/5 p-6 sm:p-8 rounded-sm">
                    <h2 className="font-serif text-2xl mb-2">Review the agreement</h2>
                    <p className="text-xs text-slate-500 mb-6">A complete PDF copy will be emailed to you upon submission.</p>
                    <div className="space-y-4 max-h-96 overflow-y-auto pr-2 border-l-2 border-gold/30 pl-5">
                        {AGREEMENT_SECTIONS.map((s) => (
                            <div key={s.title}>
                                <div className="font-medium text-gold/90 text-sm">{s.title}</div>
                                <p className="text-sm text-slate-300 leading-relaxed mt-1">{s.text}</p>
                            </div>
                        ))}
                        <p className="text-xs text-slate-500 italic mt-4">This summary is for convenience. The full legal agreement is in the emailed PDF.</p>
                    </div>
                </section>

                {/* Section 3: Sign */}
                <section className="bg-slate-900/40 border border-white/5 p-6 sm:p-8 rounded-sm">
                    <h2 className="font-serif text-2xl mb-6">Sign electronically</h2>
                    <SignaturePad onChange={setSignature} />

                    <label className="flex items-start gap-3 mt-6 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="partner-agree"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-[#D4AF37]"
                        />
                        <span className="text-sm text-slate-300 leading-relaxed">
                            I have read and agree to the Butler Room Independent Cleaning Contractor Agreement. I understand I am applying as a 1099 independent contractor and not an employee. My electronic signature above shall be deemed valid and binding under the E-SIGN Act and Florida UETA.
                        </span>
                    </label>

                    {error && <p data-testid="partner-error" className="mt-6 text-red-400 text-sm">{error}</p>}
                    <div className="flex flex-wrap gap-3 mt-8">
                        <button data-testid="partner-submit" disabled={submitting} className="btn-gold disabled:opacity-50">
                            {submitting ? "Submitting…" : "Submit & sign"}
                        </button>
                        <Link to="/partners" className="btn-ghost">Cancel</Link>
                    </div>
                </section>
            </form>
        </div>
    );
}

function Field({ label, testid, value, onChange, type = "text", className = "", placeholder, maxLength }) {
    return (
        <div className={className}>
            <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
            <input
                data-testid={testid}
                type={type} value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder} maxLength={maxLength}
                required={label.endsWith("*")}
                className="mt-2 w-full bg-slate-950 border border-white/10 rounded-sm px-4 py-3 focus:outline-none focus:border-gold"
            />
        </div>
    );
}
